import { Checkbox, Button, Upload, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import { MSTContext } from "../../../stores/Root";
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  MouseEvent,
} from "react";
import {
  PlayCircleFilledIcon,
  PauseCircleFilledIcon,
  UploadIcon,
  DeleteIcon,
} from "../../../icons-and-animations/icons";
import { SpinnerIcon } from "../../../assets/Icons";

import "./AudioOverlay.scss";
import { RcFile } from "antd/es/upload";

type AudioOverlayProps = {
  overlayAudio?: {
    url: string;
    loop: boolean;
    volume: number;
  };
  handleOverlayAudioChange: React.Dispatch<
    React.SetStateAction<{
      url: string;
      loop: boolean;
      volume: number;
    }>
  >;
  layout?: "short" | "full";
};

export const AudioOverlay = observer(
  ({
    overlayAudio = { url: "", loop: true, volume: 1 },
    handleOverlayAudioChange,
    layout = "full",
  }: AudioOverlayProps) => {
    const { uploadAssetToProject } = useContext(MSTContext).liveControlStore;
    const [isUploading, setIsUploading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
      return () => {
        mountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => setIsPlaying(false);

      audio.addEventListener("play", handlePlay);
      audio.addEventListener("playing", handlePlay);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("ended", handleEnded);

      setIsPlaying(!audio.paused);

      return () => {
        audio.removeEventListener("play", handlePlay);
        audio.removeEventListener("playing", handlePlay);
        audio.removeEventListener("pause", handlePause);
        audio.removeEventListener("ended", handleEnded);
      };
    }, [overlayAudio.url]);

    const audioName = useMemo(() => {
      const match = overlayAudio.url.match(
        /assets\/audio\/(?:[^_]+_)?(.+?)\.mp3$/
      );
      return match?.[1] ?? "audio";
    }, [overlayAudio.url]);

    const handlePlayClick = (e?: MouseEvent<HTMLSpanElement>) => {
      e?.preventDefault();
      const audio = audioRef.current;
      if (!audio) return;

      if (isPlaying) {
        audio.pause();
      } else {
        audio.play().catch((err) => {
          console.error("Error attempting to play audio:", err);
          setIsPlaying(false);
        });
      }
    };

    const handleUpload = async (file: RcFile) => {
      if (!mountedRef.current) return;

      setIsUploading(true);
      try {
        await uploadAssetToProject(
          file,
          { s3Folder: (file.type || "unknown").split("/")[0] },
          (key) => {
            if (mountedRef.current) {
              audioRef.current?.pause();
              handleOverlayAudioChange(
                layout === "short"
                  ? (key as any)
                  : { ...overlayAudio, url: key }
              );
              setIsUploading(false);
            }
          }
        );
      } catch (e) {
        if (mountedRef.current) setIsUploading(false);
        console.error("Audio upload error", e);
        throw e;
      }
    };

    return (
      <div
        className={`audio-overlay-wrapper ${layout}`}
        key="branding-audio-overlay"
      >
        {layout === "full" && (
          <div className="audio-overlay-title">Soundtrack</div>
        )}

        {overlayAudio.url && (
          <audio
            ref={audioRef}
            src={overlayAudio.url}
            loop={overlayAudio.loop ?? true}
            preload="auto"
          ></audio>
        )}

        <div className="audio-overlay-container">
          {overlayAudio.url ? (
            <>
              <div className="audio-overlay-container-header">
                <span className="audio-icon-wrapper" onClick={handlePlayClick}>
                  {isPlaying ? (
                    <PauseCircleFilledIcon />
                  ) : (
                    <PlayCircleFilledIcon />
                  )}
                </span>
                <Tooltip title={audioName}>
                  <span className="audio-name">{audioName}</span>
                </Tooltip>
              </div>
              <div className="audio-overlay-container-body">
                <div
                  className="loop-checkbox-container"
                  style={layout === "full" ? undefined : { display: "none" }}
                >
                  <label style={{ width: "110px" }}>Loop</label>
                  <Checkbox
                    checked={
                      overlayAudio.loop != null ? overlayAudio.loop : true
                    }
                    onChange={(e) => {
                      handleOverlayAudioChange({
                        ...overlayAudio,
                        loop: e.target.checked,
                      });
                      if (audioRef.current) {
                        audioRef.current.loop = e.target.checked;
                      }
                    }}
                  />
                </div>

                <div className="audio-buttons-container">
                  <Tooltip title="Replace">
                    <span className="audio-button-upload">
                      <UploadButton
                        isUploading={isUploading}
                        onUpload={handleUpload}
                        buttonType="icon"
                        mimeType="audio/mp3"
                      />
                    </span>
                  </Tooltip>

                  <Tooltip title="Remove">
                    <Button
                      style={
                        layout === "full" ? undefined : { display: "none" }
                      }
                      className="audio-button-delete"
                      icon={<DeleteIcon />}
                      onClick={() => {
                        audioRef.current?.pause();
                        handleOverlayAudioChange({
                          url: "",
                          loop: true,
                          volume: 1,
                        });
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            </>
          ) : (
            <UploadButton
              isUploading={isUploading}
              onUpload={handleUpload}
              buttonType="button"
              buttonText="Upload Audio File"
              mimeType="audio/mp3"
            />
          )}
        </div>
      </div>
    );
  }
);

type PropsUpload = {
  isUploading: boolean;
  onUpload: (file: RcFile) => Promise<void>;
  mimeType?: string;
  buttonType?: "icon" | "button";
  buttonText?: string;
};

const UploadButton = (props: PropsUpload) => {
  const { isUploading, onUpload, mimeType, buttonType, buttonText } = props;

  return (
    <Upload
      accept={mimeType}
      className="upload-button"
      showUploadList={false}
      customRequest={async ({ file, onError, onSuccess }) => {
        try {
          await onUpload(file as RcFile);
        } catch (e) {
          console.error("Audio upload error in customRequest", e);
        }
      }}
    >
      {isUploading ? (
        <SpinnerIcon />
      ) : buttonType === "icon" ? (
        <UploadIcon />
      ) : (
        <Button className="upload-button-text">{buttonText}</Button>
      )}
    </Upload>
  );
};
